import React, { useState } from 'react';
import { Container, Button, Modal, Form } from 'react-bootstrap';
import ReportsTable from '../../../components/tables/ReportsTable';
import { showToast } from '../../../utils/toast';

const ReportsOutstandingSummary = () => {
  const generateSampleData = (count = 75) => {
    const regions = ['North', 'South', 'East', 'West', 'Central'];

    return Array.from({ length: count }, (_, index) => {
      const creditLimit = Math.floor(Math.random() * 60000) + 30000;
      const outstanding = Math.floor(Math.random() * creditLimit * 0.9);
      const totalCredit = creditLimit + Math.floor(Math.random() * 80000);
      const totalDebit = totalCredit - outstanding;
      const ageingDays = Math.floor(Math.random() * 45) + 1;
      const utilisation = outstanding === 0 ? 0 : parseFloat(((outstanding / creditLimit) * 100).toFixed(1));
      const riskLevel = outstanding / creditLimit > 0.6 ? 'High' : outstanding / creditLimit > 0.4 ? 'Medium' : 'Low';

      return {
        recordId: `OUTSUM${String(index + 1).padStart(5, '0')}`,
        distributorCode: `DIS${String(Math.floor(Math.random() * 9000) + 1000)}`,
        distributorName: `Distributor ${Math.floor(Math.random() * 150) + 1}`,
        region: regions[Math.floor(Math.random() * regions.length)],
        retailersMapped: Math.floor(Math.random() * 240) + 30,
        lastCleared: new Date(2025, Math.floor(Math.random() * 10), Math.floor(Math.random() * 28) + 1)
          .toISOString()
          .split('T')[0],
        outstanding,
        totalCredit,
        totalDebit,
        creditLimit,
        utilisation,
        ageingDays,
        riskLevel,
        remarks:
          riskLevel === 'High'
            ? 'Escalated to finance desk'
            : riskLevel === 'Medium'
            ? 'Remittance reminder shared'
            : 'Stable',
      };
    });
  };

  const [records, setRecords] = useState(generateSampleData(80));
  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [clearForm, setClearForm] = useState({ amount: '', tpin: '' });

  const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;

  const getRiskVariant = (riskLevel) => {
    if (riskLevel === 'High') return 'danger';
    if (riskLevel === 'Medium') return 'warning';
    return 'success';
  };

  const handleOpenModal = (record) => {
    setSelectedRecord(record);
    setClearForm({
      amount: Number(record.outstanding || 0).toFixed(2),
      tpin: '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
    setClearForm({ amount: '', tpin: '' });
  };

  const handleSubmitClear = (event) => {
    event.preventDefault();
    if (!selectedRecord) return;

    const latestRecord = records.find((item) => item.recordId === selectedRecord.recordId);
    if (!latestRecord) {
      handleCloseModal();
      return;
    }

    const amount = parseFloat(clearForm.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      showToast.error('Enter a valid clear amount');
      return;
    }

    if (amount > latestRecord.outstanding) {
      showToast.error('Clear amount cannot exceed outstanding balance');
      return;
    }

    if (!clearForm.tpin || clearForm.tpin.length !== 4) {
      showToast.error('Enter a 4-digit T-PIN');
      return;
    }

    const clearedAt = new Date().toISOString().split('T')[0];

    setRecords((prev) =>
      prev.map((item) => {
        if (item.recordId !== latestRecord.recordId) {
          return item;
        }

        const updatedOutstanding = Math.max(0, Number(item.outstanding) - amount);
        const updatedTotalDebit = Number(item.totalDebit) + amount;
        const updatedUtilisation = item.creditLimit === 0
          ? 0
          : parseFloat(((updatedOutstanding / item.creditLimit) * 100).toFixed(1));
        const updatedRisk = updatedOutstanding / item.creditLimit > 0.6
          ? 'High'
          : updatedOutstanding / item.creditLimit > 0.4
          ? 'Medium'
          : 'Low';

        return {
          ...item,
          outstanding: updatedOutstanding,
          totalDebit: updatedTotalDebit,
          utilisation: updatedUtilisation,
          riskLevel: updatedRisk,
          lastCleared: clearedAt,
          remarks: updatedOutstanding === 0 ? 'Cleared via network adjustment' : item.remarks,
        };
      })
    );

    showToast.transactionSuccess('Outstanding cleared successfully!');
    handleCloseModal();
  };

  const columns = [
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <Button
          size="sm"
          variant="outline-primary"
          disabled={Number(row.outstanding) <= 0}
          onClick={() => handleOpenModal(row)}
        >
          Clear
        </Button>
      ),
    },
    { key: 'recordId', label: 'Record ID' },
    { key: 'distributorCode', label: 'Distributor Code' },
    { key: 'distributorName', label: 'Distributor Name' },
    { key: 'region', label: 'Region' },
    { key: 'retailersMapped', label: 'Retailers Mapped' },
    { key: 'lastCleared', label: 'Last Cleared On' },
    {
      key: 'totalCredit',
      label: 'Total Credit',
      render: (row, value) => formatCurrency(value),
    },
    {
      key: 'totalDebit',
      label: 'Total Debit',
      render: (row, value) => formatCurrency(value),
    },
    {
      key: 'outstanding',
      label: 'Outstanding',
      render: (row, value) => (
        <span className={value > 0 ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      key: 'creditLimit',
      label: 'Credit Limit',
      render: (row, value) => formatCurrency(value),
    },
    {
      key: 'utilisation',
      label: 'Utilisation %',
      render: (row, value) => `${value.toFixed ? value.toFixed(1) : Number(value).toFixed(1)}%`,
    },
    { key: 'ageingDays', label: 'Ageing (Days)' },
    { key: 'remarks', label: 'Remarks' },
  ];

  return (
    <Container fluid>
      <ReportsTable
        data={records}
        reportType="SuperDistributor_OutstandingSummary"
        columns={columns}
        title="Outstanding Overview"
        cellPaddingY={6}
      />

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Clear Outstanding</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <Form onSubmit={handleSubmitClear}>
              <div className="mb-3">
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Distributor</span>
                  <strong>{selectedRecord.distributorName}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Outstanding</span>
                  <strong>{formatCurrency(selectedRecord.outstanding)}</strong>
                </div>
              </div>
              <Form.Group className="mb-3" controlId="clear-amount">
                <Form.Label>Clear Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                  max={selectedRecord.outstanding}
                  value={clearForm.amount}
                  onChange={(e) => setClearForm((prev) => ({ ...prev, amount: e.target.value }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="clear-tpin">
                <Form.Label>T-PIN</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter 4-digit T-PIN"
                  maxLength={4}
                  value={clearForm.tpin}
                  onChange={(e) => setClearForm((prev) => ({ ...prev, tpin: e.target.value }))}
                  required
                />
              </Form.Group>
              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  Submit
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ReportsOutstandingSummary;

